import { DownOutlined, UploadOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Col,
  Form,
  FormProps,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  notification,
  Radio,
  Row,
  Select,
  Upload,
} from 'antd'
import dayjs from 'dayjs'
import { ButtonListFooter } from '../components/buttonsList'
import { DefaultDatePicker, getDateValueProps } from '../components/datePicker'
import { TOKEN_KEY } from '../../components/providers/authProvider'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ICompany } from '../../interfaces/companies'
import { IBank } from '../../interfaces/banks'
import { CrudFilter } from '@refinedev/core'
import { IBankAccount } from '../../interfaces/bankAccounts'
import { IPaymentFormValues } from '../components/interface'
import PaymentStatus from '../../types/paymentStatus.type'
import ICurrency, {
  CurrencyEWalletType,
} from '../currencies/currency.interface'
import { acceptUploadFileTypes } from '../../utils/acceptUploadFileTypes'
import { beforeUpload } from '../../utils/beforeUpload'
import Details from '../components/details/details'
import {
  bankDetailsFields,
  companyDetailsFields,
  customerDetailsFields,
  remitterDetailsFields,
} from '../components/details/details.constants'
import {
  dateOfTransactionFieldStyle,
  inputFormStyles,
  uploadButtonStyle,
} from '../styles/style'
import {
  amountRules,
  bankAccountRules,
  bankRules,
  clearFieldErrorMessageOnChange,
  currencyRules,
  customerRules,
  dateOfTransactionRules,
  getFileListFromEvent,
  remitterRules,
  serviceProviderRules,
} from '../../utils/form.constants'
import { amountFormatter, amountParser } from '../../utils/amount.utils'
import { useNotificationOkCancel } from '../../utils/hooks/useNotificationOkCancel.hook'
import {
  confirmModalDifferentCurrenciesProps,
  confirmModalUnsavedChangesProps,
} from '../components/constants'
import {
  AutoComplete,
  AutoCompleteBankInput,
} from '../../components/inputs/autocomplete'
import { IRemitter } from '../../interfaces/remitter'
import { useModalForm } from 'sunflower-antd'
import { NewRemitterModalCreate } from './addRemitter'
import { UploadChangeParam } from 'antd/es/upload'
import { AutocompleteCustomerInput } from '../../components/inputs/autocomplete/autocompleteCustomerInput'
import { ICustomer } from '../../interfaces/customer'
import { useIsAdminRole } from '../../utils/hooks/useIdentityAdmin.hook'
import { Option } from '../../components/inputs/autocomplete/autocomplete'
import {
  mapBankAccountItem,
  mapCompanyItem,
  mapCurrencyItem,
  mapRemitterItem,
} from '../../utils/edit.constants'
import {
  createPaymentOrderStatuses,
  getStatusOptionLabel,
} from '../../components/inputs/select/statusSelect.constants'
import { SelectField } from '../../components/inputs/select/select'
import { ROOT_URL } from '../../App'
import { ETHPrice, PaymentTypes } from '../../common/payment-types.const'
import { formatEther } from 'ethers'
import { getETHPrice, getUserWallet } from '../../utils/blockchain'
import {
  estimateMintGas,
  getPayTokenContract,
  mintTokens,
} from '../../common/smart-contracts/helpers/PayToken/PayToken'
import { PaymentConfirmationModal } from '../../components/modal/paymentConfirmationModal'
import { PayToken } from '../../common/smart-contracts/typechain-types'
// import { roundOff } from '../../utils/roundOff';
// import { convertScientificToNormal } from '../../utils/scientificToNormal';
import { useWatch } from 'antd/es/form/Form'

interface Props {
  formProps: FormProps
  modalProps: ModalProps
  formIsLoading: boolean
  formSubmit: (data: any) => void
  closeModal: () => void
}

export const PaymentOrderListCreate = ({
  modalProps,
  formProps,
  formIsLoading,
  formSubmit,
  closeModal,
}: Props) => {
  const isAdminRole = useIsAdminRole()

  const [showCustomerDetails, setShowCustomerDetails] = useState(false)
  const [showRemitterDetails, setShowRemitterDetails] = useState(false)
  const [showCompanyDetails, setShowCompanyDetails] = useState(false)
  const [showBankDetails, setShowBankDetails] = useState(false)
  const [nextStatus, setNextStatus] = useState<PaymentStatus>(
    PaymentStatus.DRAFT
  )
  const [currencyData, setCurrencyData] = useState<ICurrency>()
  const [customerData, setCustomerData] = useState<ICustomer>()
  const [companyData, setCompanyData] = useState<ICompany>()
  const [remitterData, setRemitterData] = useState<IRemitter>()
  const [bankData, setBankData] = useState<IBank>()
  const [accountData, setAccountData] = useState<IBankAccount>()
  const [amountData, setAmountData] = useState<string | null>(null)
  const [instructionsData, setInstructionsData] = useState<string | undefined>()
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [isFormEmpty, setIsFormEmpty] = useState(true)
  const [paymentType, setPaymentType] = useState<PaymentTypes>(
    PaymentTypes.Fiat
  )

  const [paymentConfirmationModalOpened, setPaymentConfirmationModalOpened] =
    useState<boolean>(false)

  const [api, alertContextHolder] = notification.useNotification({
    placement: 'bottom',
  })

  const [userWallet, setUserWallet] = useState<string>()
  const [payTokenContract, setPayTokenContract] = useState<PayToken>()
  const [fee, setFee] = useState<string>()
  const paymentConfirmed = useRef<(value: PromiseLike<null> | null) => void>()
  const paymentRejected = useRef<(reason?: any) => void>()
  const [ethPrice, setETHPrice] = useState<ETHPrice>()
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null)

  formProps.onFieldsChange = (_, allFields) => {
    const fields = allFields.slice(1) // remove dateOfTransaction
    const filledFields = fields.filter(
      (field) =>
        field.value !== null && field.value !== undefined && field.value !== ''
    )
    setIsFormEmpty(filledFields.length === 0)
  }

  const [remitterCreateForm] = Form.useForm<IRemitter>()
  const {
    close: createRemitterModalClose,
    modalProps: createRemitterModalProps,
    formProps: createRemitterFormProps,
    show: createRemitterModalShow,
    formLoading: createRemitterFormLoading,
    submit: createRemitterFormSubmit,
  } = useModalForm({
    autoSubmitClose: true,
    form: remitterCreateForm,
    submit: () => {
      createRemitterModalClose()
    },
  })

  const [form] = Form.useForm()
  const amount = useWatch('amount', { form })
  const cryptoWallet = useWatch('cryptoWallet', { form })
  const currencyCode: string = useWatch('currencyCode', { form })

  useEffect(() => {
    if (!ethPrice) {
      (async () => {
        try {
          const ethPrice = await getETHPrice()
          if (ethPrice.status === 200) {
            setETHPrice(ethPrice.data)
          }
        } catch (error) {
          console.error('Error fetching ETH price:', error)
        }
      })()
    }
  }, [ethPrice])

  useEffect(() => {
    form.setFieldValue('dateOfTransaction', dayjs())
    form.setFieldValue('status', PaymentStatus.DRAFT)
  }, [form])

  useEffect(() => {
    (async () => {
      try {
        const userWallet = await getUserWallet()
        if (userWallet.status === 200) {
          setUserWallet(userWallet?.data?.wallet)
        }
      } catch (error) {
        console.error('Error fetching user wallet:', error)
      }
    })()
  }, [])

  useEffect(() => {
    if (userWallet) {
      (async () => {
        try {
          const contract = await getPayTokenContract(userWallet)
          if (contract) {
            setPayTokenContract(contract)
          }
        } catch (error) {
          console.error('Error getting pay token contract:', error)
        }
      })()
    }
  }, [userWallet])

  useEffect(() => {
    if (paymentType === PaymentTypes.EWallet) {
      (async () => {
        try {
          const ethPrice = await getETHPrice()
          if (ethPrice.status === 200) {
            setETHPrice(ethPrice.data)
          }
        } catch (error) {
          console.error('Error fetching ETH price for EWallet:', error)
        }
      })()
    }
  }, [paymentType])

  const calculateMintFee = useCallback(
    async (value: string) => {
      if (userWallet) {
        const contract = await getPayTokenContract(userWallet)
        const cryptoWallet = form.getFieldValue('cryptoWallet')
        if (contract && cryptoWallet) {
          const fee = await estimateMintGas(contract, cryptoWallet, value)

          if (fee) {
            setFee(formatEther(fee))
          }
        }
      }
    },
    [userWallet, form]
  )

  useEffect(() => {
    if (cryptoWallet && userWallet) {
      if (amount) {
        if (amount > 0) {
          calculateMintFee(amount.toString())
        } else {
          setFee('')
        }
      } else {
        setFee('')
      }
    }
  }, [userWallet, amount, cryptoWallet, calculateMintFee])

  const mint = useCallback(async () => {
    const formData = form?.getFieldsValue()
    if (
      payTokenContract &&
      userWallet &&
      formData.amount &&
      formData.cryptoWallet
    ) {
      try {
        await mintTokens(
          payTokenContract,
          formData.cryptoWallet,
          formData.amount.toString()
        )
      } catch (e: any) {
        if (
          e?.info?.error?.code === -32000 ||
          e?.info?.error?.data?.code === -32000
        ) {
          api['error']({
            message: 'Transaction error',
            description: 'There is not enough funds on your balance',
          })
        } else if (
          e?.info?.error?.code === 4001 ||
          e?.info?.error?.data?.code === 4001
        ) {
          api['warning']({
            message: 'Operation cancelled',
            description: 'Transaction was cancelled by user',
          })
        } else {
          api['error']({
            message: 'Transaction error',
            description: 'Something went wrong',
          })
        }

        throw e
      }
    } else {
      api['error']({
        message: 'Transaction error',
        description: 'Something went wrong',
      })

      return Promise.reject()
    }
  }, [form, userWallet, payTokenContract, api])

  async function submitPayment() {
    if (paymentConfirmed.current && paymentRejected.current) {
      try {
        await mint()
        setPaymentConfirmationModalOpened(false)
        paymentConfirmed.current(null)
      } catch {
        setPaymentConfirmationModalOpened(false)
        paymentRejected.current()
      }
    }
  }

  const changeCustomerData = useCallback(
    (data: ICustomer) => {
      setCustomerData(data)
      form.setFieldValue('customerId', data.fullName)
    },
    [form]
  )

  const onSelectCustomer = useCallback(
    (_: ICustomer, { data }: Option<ICustomer>) => {
      changeCustomerData(data)
    },
    [changeCustomerData]
  )

  const changeCompanyData = useCallback(
    (data: ICompany) => {
      if (data.id !== companyData?.id) {
        setCompanyData(data)
        setAccountData(undefined)
        setBankData(undefined)
        form.setFieldValue('bankId', '')
        form.setFieldValue('bankAccountId', '')
      }
    },
    [companyData, form]
  )

  const changeRemitterData = useCallback(
    (data: IRemitter) => {
      if (data.id !== remitterData?.id || data.id === undefined) {
        setRemitterData(data)
        form.setFieldValue('remitterId', data.name)
      }
    },
    [remitterData, form]
  )

  const clearRemitterData = useCallback(() => {
    setRemitterData(undefined)
  }, [])

  const changeBankData = useCallback(
    (data: IBank) => {
      if (data.id !== bankData?.id) {
        setBankData(data)
        setAccountData(undefined)
        form.setFieldValue('bankAccountId', '')
      }
    },
    [bankData, form]
  )

  const clearCurrencyData = useCallback(() => setCurrencyData(undefined), [])

  const clearCustomerData = useCallback(() => {
    setCustomerData(undefined)
  }, [])

  const clearCompanyData = useCallback(() => {
    setCompanyData(undefined)
    setAccountData(undefined)
    setBankData(undefined)
    form.setFieldValue('bankId', '')
    form.setFieldValue('bankAccountId', '')
  }, [form])

  const clearBankData = useCallback(() => {
    setBankData(undefined)
    form.setFieldValue('bankAccountId', '')
  }, [form])

  const clearBankAccountData = useCallback(() => {
    setAccountData(undefined)
  }, [])

  const onSubmit = useCallback(
    async (formValues: IPaymentFormValues) => {
      const data = {
        dateOfTransaction: formValues.dateOfTransaction,
        amount: formValues.amount,
        currencyCode:
          paymentType === PaymentTypes.EWallet
            ? currencyCode
            : currencyData?.code,
        customerId: customerData && isAdminRole ? customerData?.id : undefined,
        companyId: companyData?.id,
        remitterId: remitterData?.id,
        remitter: remitterData?.id ? undefined : remitterData,
        bankId: bankData?.id,
        bankAccountId: accountData?.id,
        specialInstructions: formValues.specialInstructions,
        documents: formValues.documents
          ? formValues.documents.map(({ response }) => response)
          : undefined,
        status: nextStatus,
        svPayment: paymentType === PaymentTypes.EWallet,
        cryptoWallet: formValues.cryptoWallet || null,
      }

      if (
        data.svPayment &&
        data.status === PaymentStatus.COMPLETED &&
        data.cryptoWallet
      ) {
        if (!userWallet) {
          return api['error']({
            message: 'Transaction error',
            description:
              "You don't have a connected wallet. Connect your wallet and try again.",
          })
        }

        if (!remitterData?.connectedWallet) {
          const companyName = remitterData?.name
          return api['error']({
            message: 'Could not transfer tokens',
            description: `${companyName} has not yet connected their wallet`,
          })
        }

        setPaymentConfirmationModalOpened(true)

        try {
          await new Promise<null>((resolve, reject) => {
            paymentConfirmed.current = resolve
            paymentRejected.current = reject
          })
        } catch {
          return
        }
      }

      formSubmit(data)
      form.resetFields()
      setCurrencyData(undefined)
      setRemitterData(undefined)
      setCompanyData(undefined)
      setBankData(undefined)
      closeModal()
    },
    [
      paymentType,
      currencyCode,
      currencyData?.code,
      customerData,
      isAdminRole,
      companyData?.id,
      remitterData,
      bankData?.id,
      accountData?.id,
      nextStatus,
      formSubmit,
      form,
      closeModal,
      api,
      userWallet,
    ]
  )

  const {
    openNotification: openDifferentCurrenciesNotification,
    contextHolder: differentCurrenciesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalDifferentCurrenciesProps,
    onOk: () => {
      form.submit()
    },
  })

  const handleSubmit = useCallback(
    (buttonKey: 'save' | 'send') => async () => {
      const nextStatus = isAdminRole
        ? form.getFieldValue('status')
        : buttonKey === 'save'
          ? PaymentStatus.DRAFT
          : PaymentStatus.PENDING

      try {
        setNextStatus(nextStatus)
        if (
          accountData?.currencyCode !== undefined &&
          currencyData?.code !== undefined &&
          accountData?.currencyCode !== currencyData?.code
        ) {
          openDifferentCurrenciesNotification()
        } else {
          form.submit()
        }
      } catch (error) {
        form.submit()
      }
    },
    [
      isAdminRole,
      form,
      accountData?.currencyCode,
      currencyData?.code,
      openDifferentCurrenciesNotification,
    ]
  )

  const companyFilter = useMemo(
    (): CrudFilter => ({
      field: 'companyId',
      operator: 'eq',
      value: companyData?.id,
    }),
    [companyData]
  )

  const onlyBankCompanyFilter: CrudFilter = {
    field: '_onlyCompanyBanks',
    operator: 'eq',
    value: true,
  }

  const bankFilter = useMemo(
    (): CrudFilter => ({
      field: 'bankId',
      operator: 'eq',
      value: bankData?.id,
    }),
    [bankData]
  )

  const onUnsavedChangesOk = useCallback(() => {
    form.resetFields()
    clearCurrencyData()
    clearCompanyData()
    clearBankData()
    clearBankAccountData()
    closeModal()
    setAmountData(null)
    setInstructionsData(undefined)
  }, [
    clearBankAccountData,
    clearBankData,
    clearCompanyData,
    clearCurrencyData,
    closeModal,
    form,
  ])

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onUnsavedChangesOk,
  })

  const onModalCancel = useCallback(() => {
    const isFullFields =
      currencyData ||
      bankData ||
      companyData ||
      accountData ||
      amountData ||
      instructionsData

    if (isFullFields) {
      openUnsavedChangesNotification()
    } else {
      onUnsavedChangesOk()
    }
  }, [
    currencyData,
    bankData,
    companyData,
    accountData,
    amountData,
    instructionsData,
    openUnsavedChangesNotification,
    onUnsavedChangesOk,
  ])

  const onChangeUpload = useCallback(
    ({ file: { status } }: UploadChangeParam) => {
      setIsUploading(status === 'uploading' || status === 'error')
    },
    []
  )

  function getPaymentDetails() {
    if (paymentType === PaymentTypes.Fiat) {
      return (
        <>
          <Form.Item
            name='remitterId'
            rules={remitterRules(remitterData, nextStatus)}>
            <AutoComplete
              resource='api/v1/companies/non-defaults'
              mapItem={mapRemitterItem}
              dropdownButtonLabel='Add new remitter'
              onClickDropdownButton={createRemitterModalShow}
              onSelect={(_, { data }) => changeRemitterData(data)}
              allowClear
              onClear={clearRemitterData}
              refetchOnEmptyValue>
              <Input placeholder='Remitter' size='large' suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
          <Details
            show={showRemitterDetails}
            setShow={setShowRemitterDetails}
            name={'remitter'}
            fields={remitterDetailsFields}
            data={remitterData}
            listItemStyles={{ marginBottom: 0 }}
          />
          {isAdminRole && (
            <>
              <Form.Item name='customerId' rules={customerRules(customerData)}>
                <AutocompleteCustomerInput
                  onSelect={onSelectCustomer}
                  refetchOnEmptyValue
                  onClear={clearCustomerData}
                  allowClear
                />
              </Form.Item>
              <Details
                show={showCustomerDetails}
                setShow={setShowCustomerDetails}
                name={'customer'}
                fields={customerDetailsFields}
                data={customerData}
              />
            </>
          )}
          <Form.Item
            name='companyId'
            rules={serviceProviderRules(companyData, nextStatus)}>
            <AutoComplete
              resource='api/v1/companies/defaults'
              mapItem={mapCompanyItem}
              onSelect={(_, { data }) => changeCompanyData(data)}
              allowClear
              onClear={clearCompanyData}
              refetchOnEmptyValue>
              <Input placeholder='Service provider' size='large' suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
          <Details
            show={showCompanyDetails}
            setShow={setShowCompanyDetails}
            name={'provider'}
            fields={companyDetailsFields}
            data={companyData}
          />
          <Form.Item name='bankId' rules={bankRules(bankData, nextStatus)}>
            <AutoCompleteBankInput
              disabled={companyData === undefined}
              additionalFilters={[companyFilter, onlyBankCompanyFilter]}
              onSelect={(_, { data }) => changeBankData(data)}
              allowClear
              onClear={clearBankData}
              refetchOnEmptyValue
            />
          </Form.Item>
          <Details
            show={showBankDetails}
            setShow={setShowBankDetails}
            name={'bank'}
            fields={bankDetailsFields}
            data={bankData}
          />
          <Form.Item
            name='bankAccountId'
            rules={bankAccountRules(accountData, nextStatus)}>
            <AutoComplete
              resource='api/v1/bank_accounts'
              additionalFilters={[companyFilter, bankFilter]}
              disabled={bankData === undefined}
              mapItem={mapBankAccountItem}
              filterOption={false}
              onSelect={(_, { data }) => setAccountData(data)}
              allowClear
              onClear={clearBankAccountData}
              refetchOnEmptyValue>
              <Input placeholder='Account' size='large' suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
        </>
      )
    }

    return (
      <>
        <Form.Item name='amount'>
          <InputNumber
            placeholder='SMART tokens (ETH)'
            style={inputFormStyles}
            stringMode={false}
            controls={false}
            onChange={async (value) => {
              if (value && (currencyCode === 'EUR' || currencyCode === 'USD')) {
                value = parseFloat(value.toString())
                const amount = value * (ethPrice ? ethPrice[currencyCode] : 1)
                setConvertedAmount(amount)
              }
            }}
          />
        </Form.Item>
        {paymentType === PaymentTypes.EWallet && fee && (
          <Row>
            <p>Platform Fee: {fee}</p>
          </Row>
        )}
        <Form.Item
          name='remitterId'
          rules={remitterRules(remitterData, nextStatus)}>
          <AutoComplete
            resource='api/v1/companies/non-defaults'
            mapItem={mapRemitterItem}
            dropdownButtonLabel='Add new remitter'
            onClickDropdownButton={createRemitterModalShow}
            onSelect={(_, { data }) => changeRemitterData(data)}
            allowClear
            onClear={clearRemitterData}
            refetchOnEmptyValue>
            <Input placeholder='Remitter' size='large' suffix={<DownOutlined />} />
          </AutoComplete>
        </Form.Item>
        <Details
          show={showRemitterDetails}
          setShow={setShowRemitterDetails}
          name={'remitter'}
          fields={remitterDetailsFields}
          data={remitterData}
          listItemStyles={{ marginBottom: 0 }}
        />
        {isAdminRole && (
          <>
            <Form.Item name='customerId' rules={customerRules(customerData)}>
              <AutocompleteCustomerInput
                onSelect={onSelectCustomer}
                refetchOnEmptyValue
                onClear={clearCustomerData}
                allowClear
              />
            </Form.Item>
            <Details
              show={showCustomerDetails}
              setShow={setShowCustomerDetails}
              name={'customer'}
              fields={customerDetailsFields}
              data={customerData}
            />
          </>
        )}
        <Form.Item
          name='companyId'
          rules={serviceProviderRules(companyData, nextStatus)}>
          <AutoComplete
            resource='api/v1/companies/defaults'
            mapItem={mapCompanyItem}
            onSelect={(_, { data }) => changeCompanyData(data)}
            allowClear
            onClear={clearCompanyData}
            refetchOnEmptyValue>
            <Input placeholder='Service provider' size='large' suffix={<DownOutlined />} />
          </AutoComplete>
        </Form.Item>
        <Details
          show={showCompanyDetails}
          setShow={setShowCompanyDetails}
          name={'provider'}
          fields={companyDetailsFields}
          data={companyData}
        />
        <Form.Item name='cryptoWallet'>
          <Input
            style={{ fontSize: 14 }}
            placeholder="Remitter's crypto wallet"
            size='large'
          />
        </Form.Item>
        <Form.Item>
          <Alert
            style={{ marginTop: 0 }}
            message="Please make sure that the remitter's crypto wallet address is correct. If there are any typos, no refund will be given."
            type='warning'
          />
        </Form.Item>
      </>
    )
  }

  function getCurrencyDetails() {
    if (paymentType === PaymentTypes.Fiat) {
      return (
        <>
          <Form.Item
            name='amount'
            rules={amountRules(nextStatus)}
            style={{ width: '48%' }}>
            <InputNumber
              placeholder='Transaction amount'
              style={inputFormStyles}
              formatter={amountFormatter}
              parser={amountParser}
              stringMode={false}
              controls={false}
              onChange={setAmountData}
            />
          </Form.Item>
          <Form.Item
            name='currencyCode'
            rules={currencyRules(currencyData, nextStatus)}
            style={{
              width: '48%',
            }}>
            <AutoComplete
              allowClear
              resource='api/v1/currencies'
              mapItem={mapCurrencyItem}
              onSelect={(_, { data }) => setCurrencyData(data)}
              onClear={clearCurrencyData}
              refetchOnEmptyValue>
              <Input placeholder='Transaction currency' size='large' suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
        </>
      )
    }

    return (
      <>
        <Form.Item style={{ width: '48%' }}>
          <InputNumber
            placeholder='Transaction amount'
            value={
              convertedAmount !== undefined
                ? convertedAmount
                : form?.getFieldValue('amount') *
                (ethPrice &&
                  (currencyCode === 'USD' || currencyCode === 'EUR')
                  ? ethPrice[currencyCode]
                  : 1)
            }
            onChange={(value) => {
              const amount =
                (value ?? 0) /
                (ethPrice && (currencyCode === 'USD' || currencyCode === 'EUR')
                  ? ethPrice[currencyCode]
                  : 1)

              form?.setFieldValue('amount', amount)
              setConvertedAmount(value)
            }}
            controls={false}
            style={inputFormStyles}
          />
        </Form.Item>
        <Form.Item name='currencyCode' style={{ width: '48%', marginTop: 5 }}>
          <Select
            rootClassName={'select-root-manual'}
            style={{ marginTop: 0 }}
            onChange={(
              value: 'USD' | 'EUR',
              option?: CurrencyEWalletType | Array<CurrencyEWalletType>
            ) => {
              let amount = form?.getFieldValue('amount')

              if (convertedAmount) {
                form?.setFieldValue(
                  'amount',
                  convertedAmount / (ethPrice ? ethPrice[value] : 1)
                )
              } else if (amount) {
                amount = amount * (ethPrice ? ethPrice[value] : 1)
                setConvertedAmount(amount)
              }

              if (!Array.isArray(option) && option) {
                const { data, value } = option
                formProps.form?.setFieldValue('currency', { data, value })
              }
            }}
            options={[
              {
                value: 'USD',
                label: 'USD - United States Dollar',
                data: {
                  code: 'USD',
                  name: 'United States Dollar',
                },
              },
              {
                value: 'EUR',
                label: 'EUR - Euro',
                data: {
                  code: 'EUR',
                  name: 'Euro',
                },
              },
            ]}
            placeholder='Transaction currency'
          />
        </Form.Item>
      </>
    )
  }

  return (
    <>
      <Modal
        {...modalProps}
        title='Request money'
        className='smartModalTitle'
        footer={
          <ButtonListFooter
            handleSave={handleSubmit('save')}
            handleSend={!isAdminRole ? handleSubmit('send') : onModalCancel}
            isLoading={formIsLoading}
            isSaveBtnDisabled={isFormEmpty || isUploading}
            isSendBtnDisabled={isUploading}
            isAdminRole={isAdminRole}
          />
        }
        onCancel={onModalCancel}>
        {differentCurrenciesContextHolder}
        {unsavedChangesContextHolder}
        <Form
          {...formProps}
          layout='vertical'
          form={form}
          onFinish={onSubmit}
          disabled={formIsLoading}
          validateTrigger={false}
          onValuesChange={clearFieldErrorMessageOnChange(form)}>
          <Form.Item
            name='dateOfTransaction'
            rules={dateOfTransactionRules}
            getValueProps={getDateValueProps}>
            <DefaultDatePicker
              placeholder='Date of transaction'
              style={dateOfTransactionFieldStyle}
            />
          </Form.Item>
          <Col
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}>
            {getCurrencyDetails()}
          </Col>
          {/* {paymentType === PaymentTypes.EWallet && convertationCurrency && ethPrice && fee && (
            <Row>
              <p>
                Platform Fee:{' '}
                {roundOff(convertScientificToNormal((parseFloat(fee) * ethPrice[convertationCurrency]).toString()))}{' '}
                {convertationCurrency}
              </p>
            </Row>
          )} */}
          {paymentType === PaymentTypes.EWallet && (
            <Row>
              <p>Platform Fee: 0.5 {currencyCode ?? 'USD'}</p>
            </Row>
          )}
          <Radio.Group
            value={paymentType}
            onChange={(e) => {
              setPaymentType(e.target.value)

              form?.setFieldValue('cryptoWallet', null)
              form?.setFieldValue('amount', null)
              form?.setFieldValue('currencyCode', null)
              setConvertedAmount(null)
            }}>
            <Radio.Button style={{ borderRadius: 0 }} value={PaymentTypes.Fiat}>
              {PaymentTypes.Fiat}
            </Radio.Button>
            <Radio.Button
              style={{ borderRadius: 0 }}
              value={PaymentTypes.EWallet}>
              {PaymentTypes.EWallet}
            </Radio.Button>
          </Radio.Group>
          {getPaymentDetails()}
          <Form.Item
            name='specialInstructions'
            validateTrigger='onBlur'
            rules={[
              {
                max: 256,
                message: 'Maximum 256 characters',
                validateTrigger: 'onBlur',
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder='Special instructions:'
              onChange={(event) => setInstructionsData(event.target.value)}
              allowClear
            />
          </Form.Item>
          {isAdminRole && (
            <SelectField
              name='status'
              getOptionLabel={getStatusOptionLabel}
              items={createPaymentOrderStatuses}
            />
          )}
          <Col>
            <Form.Item
              name='documents'
              valuePropName='fileList'
              getValueFromEvent={getFileListFromEvent}>
              <Upload
                accept={acceptUploadFileTypes}
                action={`${ROOT_URL}/api/v1/storage/upload`}
                listType='text'
                headers={{
                  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
                }}
                multiple={true}
                beforeUpload={beforeUpload}
                onChange={onChangeUpload}>
                <Button icon={<UploadOutlined />} style={uploadButtonStyle}>
                  &nbsp;Click to upload supporting documents
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Form>
      </Modal>
      {alertContextHolder}
      <NewRemitterModalCreate
        formProps={createRemitterFormProps}
        modalProps={createRemitterModalProps}
        formIsLoading={createRemitterFormLoading}
        formSubmit={createRemitterFormSubmit}
        close={createRemitterModalClose}
        changeRemitterData={changeRemitterData}
      />
      <PaymentConfirmationModal
        modalOpened={paymentConfirmationModalOpened}
        closeModal={() => {
          if (paymentRejected.current) {
            setPaymentConfirmationModalOpened(false)
            paymentRejected.current()
          }
        }}
        paymentDetails={{
          fee,
          userWallet,
          ethPrice,
          walletToTransferFundsTo:
            formProps.form?.getFieldValue('cryptoWallet'),
          tokensToTransfer: formProps.form?.getFieldValue('amount'),
        }}
        onSubmit={submitPayment}
      />
    </>
  )
}
